'use client';

import * as React from 'react';
import Link from 'next/link';
import { IRegionGroup, IRegionShort } from '@/api/region/types';
import { Heading } from '@/components/ui/heading';
import { cn } from '@/util/cn';
import { SectionCard } from '@/components/ui/section-card';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';

// Sectioned region directory used on global landing pages (/skireport,
// /ski-resorts, ...). Each group from `regions.groups` becomes a
// bordered card with the group title, a region count, and a multi-
// column grid of region links below.
//
// The link-target route varies by page (e.g. snow report vs trip
// planning overview), so callers pass a `linkFor` resolver. Same
// pattern the legacy ViewRegions used for cross-page reuse.

export type BrowseSection = {
  group: IRegionGroup;
  regions: Array<IRegionShort>;
};

export type BrowseByGroupProps = {
  sections: Array<BrowseSection>;
  linkFor: (region: IRegionShort | IRegionGroup) => string;
};

const RegionLinkList = ({
  regions, linkFor,
}: {
  regions: Array<IRegionShort>;
  linkFor: (r: IRegionShort) => string;
}) => (
  <ul
    className={cn(
      'grid gap-x-6 gap-y-2.5',
      'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
    )}
  >
    {regions.map((r) => (
      <li key={r.uuid}>
        <Link
          href={linkFor(r)}
          className="text-sm font-semibold text-primary hover:underline"
        >
          {r.title}
        </Link>
      </li>
    ))}
  </ul>
);

const GroupCard = ({
  section, linkFor,
}: {
  section: BrowseSection;
  linkFor: BrowseByGroupProps['linkFor'];
}) => {
  const { t } = useOTSTranslation('common');
  const { group, regions } = section;
  const headingClass = 'font-heading text-lg text-secondary md:text-xl';
  return (
    <SectionCard>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3 border-b border-border pb-3">
        {group.slug ? (
          <Link href={linkFor(group)} className="hover:underline">
            <Heading level={2} className={headingClass}>
              {group.title}
            </Heading>
          </Link>
        ) : (
          <Heading level={2} className={headingClass}>
            {group.title}
          </Heading>
        )}
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {`${regions.length} ${regions.length === 1 ? t('common.regionWord') : t('common.regionsWord')}`}
        </span>
      </div>
      <RegionLinkList regions={regions} linkFor={linkFor} />
    </SectionCard>
  );
};

export const BrowseByGroup = ({ sections, linkFor }: BrowseByGroupProps) => {
  const populated = sections.filter((s) => s.regions.length > 0);
  if (populated.length === 0) return null;
  return (
    <div className="space-y-5">
      {populated.map((section) => (
        <GroupCard key={section.group.uuid} section={section} linkFor={linkFor} />
      ))}
    </div>
  );
};
